import FormattedPropertyV2 from 'customer-data-properties/formatted/FormattedPropertyV2';
import { USER, OWNER } from 'reference-resolvers-lite/constants/ReferenceTypes';
import { isCompany, isOwner, isUser, isContactEmail, isDomain, isMultienum } from 'customer-data-objects/property/PropertyIdentifier';
import ResolvedUserCell from './ResolvedUserCell';
import { isPropertySourceUserId, isFile } from 'customer-data-objects/property/PropertyIdentifier';
import { parsePropertySourceUserId } from 'customer-data-objects/property/PropertySourceIdentifier';
import ResolvedCrmObjectCell from './ResolvedCrmObjectCell';
import { COMPANY_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { denormalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { useAccessLevel } from 'customer-data-properties/accessLevel/AccessLevelContext';
import { HIDDEN, NOT_SPECIFIED } from 'customer-data-objects/property/AccessLevel';
import emptyFunction from 'react-utils/emptyFunction';
import { useCallback, useState } from 'react';
import ClickToAssignModal from './ClickToAssignModal';
import { useAuth } from '../../auth/context/AuthContext';
import FileCell from '../FileCell';
import { CRM_PROPERTY_DATE_FORMAT, CRM_PROPERTY_DATE_TIME_FORMAT } from './constants';
import { stopPropagationHandler } from 'foundations-components/transitional/utils/DomEvents';
import { Link } from 'foundations-components/transitional/components/Link';
import { Button } from 'foundations-components/transitional/components/Button';
import { Metrics } from '../../metrics/Metrics';
import { View } from 'foundations-components/transitional/components/View';
import { gate } from 'hub-http/gates';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const OWNER_PROPERTY_NAME = 'hubspot_owner_id';
const BET_PORTAL_SCOPE = 'bet-enforce-deal-stage-restrictions';
const alwaysUndefined = emptyFunction.thatReturns(undefined);
const domainProtocolRegex = /^https?:/i;
const normalizeDomain = rawDomain => domainProtocolRegex.test(rawDomain) ? rawDomain : `https://${rawDomain}`;
const isLinkCell = (propertyDefinition, objectType) => {
  return isDomain(propertyDefinition, objectType) ||
  // The old table special-cased these properties to be links regardless of object type.
  ['website', 'cleaned_domain', 'company_info_domain', 'hs_sender_company_domain'].includes(propertyDefinition.name);
};
const PropertyCellDisplayContent = ({
  value,
  objectTypeId,
  objectId,
  propertyDefinition,
  canEditObject,
  getSecondaryPropertyValue,
  onPropertyUpdatesSucceeded = emptyFunction,
  onPropertyUpdatesFailed = emptyFunction,
  onClick = emptyFunction
}) => {
  const objectType = denormalizeTypeId(objectTypeId);

  // Only used for click-to-assign
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const handleToggleClickToAssignModal = useCallback(() => {
    Metrics.counter('click_to_assign_modal_opened').increment();
    setIsAssignModalOpen(isOpen => !isOpen);
  }, []);
  const accessLevel = useAccessLevel({
    objectType: objectTypeId,
    propertyName: propertyDefinition.name
  });
  const isHidden = accessLevel === HIDDEN;

  // HACK: We don't actually need to check readOnlyValue here, since this is only
  // ever used for click-to-assign which will die with inline editing.
  const canEditProperty = !propertyDefinition.readOnlyValue && accessLevel === NOT_SPECIFIED;
  const isMultienumProperty = isMultienum(propertyDefinition);
  const {
    hasScope,
    hasGate
  } = useAuth();
  const isBETPortal = hasScope(BET_PORTAL_SCOPE);
  const hasPropertiesLinkifyGate = hasGate(gate('CRM:Properties:Linkify'));
  if (isHidden) {
    return /*#__PURE__*/_jsx(View, {
      paddingInline: "200",
      as: "span",
      children: /*#__PURE__*/_jsx(FormattedPropertyV2, {
        objectTypeId: objectTypeId,
        objectId: objectId,
        property: propertyDefinition
        // value is undefined since the user doesn't have permission to view it (hidden via FLP)
        ,
        value: undefined,
        getDraftValue: alwaysUndefined,
        getSavedValue: alwaysUndefined,
        getIsUngated: hasGate,
        autoManageHighlySensitiveValue: false
      })
    });
  }
  if (isCompany(propertyDefinition, objectType)) {
    return /*#__PURE__*/_jsx(ResolvedCrmObjectCell, {
      objectTypeId: COMPANY_TYPE_ID,
      objectId: value === null ? undefined : value
    });
  }

  // These three conditions are for handling user-type properties.
  // They prepend an avatar and append the user's email.
  if (value && isPropertySourceUserId(propertyDefinition, value) && !isMultienumProperty) {
    return /*#__PURE__*/_jsx(ResolvedUserCell, {
      id: String(parsePropertySourceUserId(value)),
      objectTypeId: objectTypeId,
      type: USER,
      propertyDefinition: propertyDefinition
    });
  }
  if (propertyDefinition.name === OWNER_PROPERTY_NAME && canEditObject && canEditProperty && !isBETPortal && !isMultienumProperty) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [isAssignModalOpen && /*#__PURE__*/_jsx(ClickToAssignModal, {
        objectTypeId: objectTypeId,
        objectId: objectId,
        currentValue: value,
        propertyDefinition: propertyDefinition,
        onClose: handleToggleClickToAssignModal,
        onSuccess: onPropertyUpdatesSucceeded,
        onFailure: onPropertyUpdatesFailed
      }), /*#__PURE__*/_jsx(Button, {
        use: "link",
        onClick: handleToggleClickToAssignModal,
        children: /*#__PURE__*/_jsx(ResolvedUserCell, {
          id: value,
          type: OWNER,
          objectTypeId: objectTypeId,
          propertyDefinition: propertyDefinition
        })
      })]
    });
  }
  if (isUser(propertyDefinition) && !isMultienumProperty) {
    return /*#__PURE__*/_jsx(ResolvedUserCell, {
      id: value,
      type: USER,
      objectTypeId: objectTypeId,
      propertyDefinition: propertyDefinition
    });
  }
  if (isOwner(propertyDefinition, objectType) && !isMultienumProperty) {
    return /*#__PURE__*/_jsx(ResolvedUserCell, {
      id: value,
      type: OWNER,
      objectTypeId: objectTypeId,
      propertyDefinition: propertyDefinition
    });
  }
  if (isFile(propertyDefinition)) {
    return /*#__PURE__*/_jsx(FileCell, {
      objectId: objectId,
      objectTypeId: objectTypeId,
      value: value,
      propertyDefinition: propertyDefinition,
      getSecondaryPropertyValue: getSecondaryPropertyValue,
      hasGate: hasGate,
      onClick: onClick
    });
  }
  const formattedContent = /*#__PURE__*/_jsx(FormattedPropertyV2, {
    objectTypeId: objectTypeId,
    objectId: objectId,
    property: propertyDefinition,
    value: value,
    getDraftValue: getSecondaryPropertyValue,
    getSavedValue: alwaysUndefined,
    dateFormat: CRM_PROPERTY_DATE_FORMAT,
    dateTimeFormat: CRM_PROPERTY_DATE_TIME_FORMAT,
    relativeDate: true,
    relativeDateTime: true,
    autoManageHighlySensitiveValue: false
  });
  if (value && isContactEmail(propertyDefinition, objectType) && !hasPropertiesLinkifyGate) {
    return /*#__PURE__*/_jsx(Link, {
      href: `mailto:${value}`,
      onClick: stopPropagationHandler,
      children: formattedContent
    });
  }
  if (value && isLinkCell(propertyDefinition, objectType) && !hasPropertiesLinkifyGate) {
    return /*#__PURE__*/_jsx(Link, {
      href: normalizeDomain(value),
      external: true,
      onClick: stopPropagationHandler,
      children: formattedContent
    });
  }
  return formattedContent;
};
export default PropertyCellDisplayContent;